import { memo, useCallback, useMemo } from 'react';

import { useLibraries } from '~/components/LibraryBeta/hooks/queries/useLibraries';
import { WorkspaceSidebarLibrariesHeader } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarLibrariesHeader';
import {
  WorkspaceSidebarLibrariesList,
  type WorkspaceSidebarLibrariesListProps,
} from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarLibrariesList';
import { WorkspaceSidebarLibrariesListItemSkeleton } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarLibrariesListItemSkeleton';
import { WorkspaceSidebarSection } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSection';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { useRearrangeLibraries } from '~/components/v2/hooks/useRearrangeLibraries';
import { useWorkspaceSidebarLibrariesStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarLibrariesStore';
import { LibrarySortBy } from '~/components/v2/store/shared/creareLibrarySortSlice';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useLibrariesBeingDeleted } from '~/swr-hooks/libraries/useLibrariesBeingDeleted';

export const WorkspaceSidebarLibraries = memo(() => {
  const librarySortBy = useWorkspaceSidebarLibrariesStore((state) => state.librarySortBy);
  const librarySortDirection = useWorkspaceSidebarLibrariesStore((state) => state.librarySortDirection);
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);
  const { data: libraries, isLoading: isLoadingLibraries } = useLibraries({
    options: {
      sortField: {
        name: librarySortBy,
        direction: librarySortDirection,
      },
    },
  });
  const librariesBeingDeleted = useLibrariesBeingDeleted();
  const { rearrangeLibraries } = useRearrangeLibraries();

  const flattenedLibraries = useMemo(
    () =>
      libraries?.pages.flatMap((page) => page.data).filter((library) => !librariesBeingDeleted.includes(library.id)) ??
      [],
    [libraries?.pages, librariesBeingDeleted],
  );

  useFetchObjectsPermissions({
    objects: {
      libraryIds: flattenedLibraries.map(({ id }) => id),
    },
  });

  const isDraggable = librarySortBy === LibrarySortBy.CustomSort;

  const onLibraryRearrange = useCallback<Required<WorkspaceSidebarLibrariesListProps>['onLibraryRearrange']>(
    ({ adjacentItem, libraries, library }) => {
      rearrangeLibraries({
        libraries,
        draggedLibrary: library.library,
        adjacentItem,
      });
    },
    [rearrangeLibraries],
  );

  return (
    <WorkspaceSidebarSection className="contents">
      <WorkspaceSidebarLibrariesHeader />

      {collapsed ? null : isLoadingLibraries ? (
        <>
          <WorkspaceSidebarLibrariesListItemSkeleton />
          <WorkspaceSidebarLibrariesListItemSkeleton />
        </>
      ) : (
        <WorkspaceSidebarLibrariesList
          libraries={flattenedLibraries}
          draggable={isDraggable}
          onLibraryRearrange={onLibraryRearrange}
        />
      )}
    </WorkspaceSidebarSection>
  );
});

WorkspaceSidebarLibraries.displayName = 'WorkspaceSidebarLibraries';
